import { useEffect, useMemo, useState } from 'react';
import useBeerIngestionFailures from './useBeerIngestionFailures';
import useBeerIngestionReviews from './useBeerIngestionReviews';
import useBeerIndex from './useBeerIndex';
import useBeerStyles from './useBeerStyles';
import { BEER_INGESTION_DEFAULT_STATE } from '../utils/callableBeerConstants';
import { filterStylesNeedingTypeMapping } from '../utils/beerStyle/styleNeedsTypeMapping';
import {
  BEER_DUPLICATE_REVIEWS_COLLECTION,
  CRAWLER_FAILURES_COLLECTION,
} from '../utils/firestoreCollections';
import { getCollectionCount } from '../utils/firestoreCounts';
import {
  readOpenFailureCount,
  sumIngestionReviewQueue,
} from '../utils/engageWorkQueueConfig';

const EMPTY_COUNT_STATE = {
  count: 0,
  loading: true,
  error: null,
};

function readSettledCount(result) {
  if (result.status === 'fulfilled') {
    return { count: Number(result.value) || 0, error: null };
  }
  const reason = result.reason;
  const message =
    typeof reason?.message === 'string' && reason.message.trim().length > 0
      ? reason.message
      : 'Failed to load queue count';
  return { count: 0, error: message };
}

/**
 * Live Engage work-queue depths for Key Metrics tiles.
 * Ingestion queues match the destination pages (default state).
 * Duplicates and data issues are global aggregation counts.
 */
export default function useEngageWorkQueueCounts() {
  const reviews = useBeerIngestionReviews(BEER_INGESTION_DEFAULT_STATE);
  const failures = useBeerIngestionFailures(BEER_INGESTION_DEFAULT_STATE);
  const {
    beerStyles,
    loading: beerStylesLoading,
    error: beerStylesError,
  } = useBeerStyles();
  const styleIdsNeedingType = useMemo(
    () => filterStylesNeedingTypeMapping(beerStyles)
      .map((style) => style.id)
      .filter(Boolean),
    [beerStyles],
  );
  const catalogReady = !beerStylesLoading && !beerStylesError;
  const beersNeedingStyleSearch = useBeerIndex('*', {
    pageSize: 1,
    styleIds: styleIdsNeedingType,
    enabled: catalogReady,
  });

  const [duplicates, setDuplicates] = useState(EMPTY_COUNT_STATE);
  const [dataIssues, setDataIssues] = useState(EMPTY_COUNT_STATE);

  useEffect(() => {
    let cancelled = false;

    const loadAggregationCounts = async () => {
      setDuplicates((previous) => ({ ...previous, loading: true, error: null }));
      setDataIssues((previous) => ({ ...previous, loading: true, error: null }));

      const [duplicatesResult, pendingIssuesResult, investigatedIssuesResult] =
        await Promise.allSettled([
          getCollectionCount(BEER_DUPLICATE_REVIEWS_COLLECTION, 'status', 'pending'),
          getCollectionCount(CRAWLER_FAILURES_COLLECTION, 'status', 'pending'),
          getCollectionCount(CRAWLER_FAILURES_COLLECTION, 'status', 'investigated'),
        ]);

      if (cancelled) {
        return;
      }

      const duplicateCount = readSettledCount(duplicatesResult);
      setDuplicates({
        count: duplicateCount.count,
        loading: false,
        error: duplicateCount.error,
      });

      const pendingIssues = readSettledCount(pendingIssuesResult);
      const investigatedIssues = readSettledCount(investigatedIssuesResult);
      const dataIssueError = pendingIssues.error || investigatedIssues.error;
      setDataIssues({
        count: dataIssueError ? 0 : pendingIssues.count + investigatedIssues.count,
        loading: false,
        error: dataIssueError,
      });
    };

    loadAggregationCounts();

    return () => {
      cancelled = true;
    };
  }, []);

  const beersNeedingStyleType = {
    count: beerStylesError ? 0 : (beersNeedingStyleSearch.found || 0),
    loading: beerStylesLoading || (catalogReady && beersNeedingStyleSearch.loading),
    error: beerStylesError || beersNeedingStyleSearch.error,
  };

  return {
    reviews: {
      count: sumIngestionReviewQueue(reviews.summary),
      loading: reviews.loading,
      error: reviews.error,
    },
    errors: {
      count: readOpenFailureCount(failures.summary),
      loading: failures.loading,
      error: failures.error,
    },
    duplicates,
    dataIssues,
    beersNeedingStyleType,
  };
}
